import _ from 'lodash';
import { addToShelf as addToShelfGA } from '@bibliocommons/ga-event-templates';
import ShelfApi from '../api/ShelfApi';
import ShelfConstants from '../constants/ShelfConstants';
import composeMessaging from '../helpers/messaging/composeMessaging';
import composeGlobalSuccessMessaging from '../helpers/messaging/composeGlobalSuccessMessaging';
import composeGlobalFailureMessaging from '../helpers/messaging/composeGlobalFailureMessaging';

function shelfItemPrivacyStatus(response, store) {
  const isShelfItemPrivate = _.values(response.entities.shelves[store.getState().getIn(['auth', 'currentUserId'])])
                              .map(shelfItem => shelfItem.privateItem)[0];

  return isShelfItemPrivate && isShelfItemPrivate ? 'private' : 'public';
}

function searchUserShelf(shelfName, userId, query) {
  return {
    types: [
      ShelfConstants.SHELF_SEARCH_REQUEST,
      ShelfConstants.SHELF_SEARCH_SUCCESS,
      ShelfConstants.SHELF_SEARCH_FAILURE
    ],
    apiRequest: (state, client) => ShelfApi.searchUserShelf({
      shelfName,
      userId,
      ...query
    }, client)
  };
}

function fetchAdditionalFilters(shelfName, userId, fieldId, query) {
  return {
    types: [
      ShelfConstants.FETCH_FILTERS_REQUEST,
      ShelfConstants.FETCH_FILTERS_SUCCESS,
      ShelfConstants.FETCH_FILTERS_FAILURE
    ],
    apiRequest: (state, client) => ShelfApi.fetchAdditionalFilters({
      shelfName,
      userId,
      fieldId,
      ...query
    }, client)
  };
}

function importILSBibs(shelfName, accountId) {
  return {
    types: [
      ShelfConstants.IMPORT_ILS_BIBS_REQUEST,
      ShelfConstants.IMPORT_ILS_BIBS_SUCCESS,
      ShelfConstants.IMPORT_ILS_BIBS_FAILURE
    ],
    middlewareData: {
      authRequired: true
    },
    apiRequest: (state, client) => ShelfApi.importILSBibs(shelfName, accountId, client)
  };
}

// TODO SHEL-191: Eventually remove isBatch conditional and always pass shelfItem in array and remove ShelfApi.updateShelfItem
function updateShelfItems({ shelfName, newShelfName, shelfItemIds, metadataIds, postSuccess, isBatch = false, ...fieldsToUpdate }) {
  return {
    isBatch,
    metadataIds,
    postSuccess,
    fields: fieldsToUpdate,
    types: [
      ShelfConstants.UPDATE_SHELF_ITEM_REQUEST,
      ShelfConstants.UPDATE_SHELF_ITEM_SUCCESS,
      ShelfConstants.UPDATE_SHELF_ITEM_FAILURE
    ],
    middlewareData: {
      authRequired: true
    },
    apiRequest: (state, client) => {
      if (isBatch) {
        return ShelfApi.updateShelfItems({
          shelfName,
          newShelfName,
          metadataIds,
          shelfItemIds,
          privateItem: fieldsToUpdate.privateItem
        }, client);
      }

      return ShelfApi.updateShelfItem({
        shelfName,
        shelf: newShelfName,
        shelfItemId: shelfItemIds[0],
        userId: state.getIn(['auth', 'currentUserId']),
        ...fieldsToUpdate
      }, client);
    },
    apiSuccess: () => ({
      messaging: isBatch ? composeGlobalSuccessMessaging('message_successful_batch_move_shelves') : null
    })
  };
}

function removeShelfItems({ shelfName, shelfItemIds, metadataIds, workflowId, postSuccess, isBatch = false }) {
  return {
    isBatch,
    workflowId,
    metadataIds,
    postSuccess,
    types: [
      ShelfConstants.REMOVE_FROM_SHELF_REQUEST,
      ShelfConstants.REMOVE_FROM_SHELF_SUCCESS,
      ShelfConstants.REMOVE_FROM_SHELF_FAILURE
    ],
    middlewareData: {
      authRequired: true
    },
    apiRequest: (state, client) => ShelfApi.removeFromShelf({
      shelfName,
      shelfItemIds,
      metadataIds,
      userId: state.getIn(['auth', 'currentUserId'])
    }, client),
    apiSuccess: (response, store) => ({
      deletedEntities: shelfItemIds.map(itemId => `shelves.${store.getState().getIn(['auth', 'currentUserId'])}.${itemId}`),
      messaging: isBatch ?
        composeGlobalSuccessMessaging('message_successful_batch_remove_shelves') :
        composeMessaging('SHELF', metadataIds[0], 'REMOVE', {
          values: { shelf: shelfName }
        })
    }),
    apiFailure: ({ error }) => ({
      messaging: isBatch ? composeGlobalFailureMessaging(error) : null
    })
  };
}

/**
 * Adds ONE bib to the specified shelf
 * @param {String} metadataId Bib to add
 * @param {String} shelfToAdd Name of the shelf to add to
 */
function addToShelf(metadataId, shelfToAdd, postSuccess = () => null) {
  return {
    metadataIds: [metadataId],
    postSuccess,
    types: [
      ShelfConstants.ADD_TO_SHELF_REQUEST,
      ShelfConstants.ADD_TO_SHELF_SUCCESS,
      ShelfConstants.ADD_TO_SHELF_FAILURE
    ],
    middlewareData: {
      authRequired: true,
      gaEvent: addToShelfGA(shelfToAdd)
    },
    apiRequest: (state, client) => ShelfApi.addToShelf({
      metadataIds: [metadataId], shelfName: shelfToAdd, userId: state.getIn(['auth', 'currentUserId'])
    }, client)
  };
}

/**
 * Adds multiple bibs to the specified shelf
 * @param {String} metadataId Bib to add
 * @param {String} shelfName Name of the shelf to add to
 */
function batchAddToShelf({ metadataIds, shelfName }) {
  return {
    isBatch: true,
    metadataIds,
    types: [
      ShelfConstants.ADD_TO_SHELF_REQUEST,
      ShelfConstants.ADD_TO_SHELF_SUCCESS,
      ShelfConstants.ADD_TO_SHELF_FAILURE
    ],
    middlewareData: {
      authRequired: true,
      gaEvent: addToShelfGA(shelfName)
    },
    apiRequest: (state, client) => ShelfApi.addToShelf({
      metadataIds,
      shelfName,
      userId: state.getIn(['auth', 'currentUserId'])
    }, client),
    apiSuccess: (response, store) => ({
      messaging: composeGlobalSuccessMessaging(`message_successful_batch_add_to_shelf_${shelfName}_${shelfItemPrivacyStatus(response, store)}`)
    })
  };
}

/**
 * Displays the post-add to shelf view for an existing shelf item
 * @param {String}
 */
function showPostAddToShelfView(metadataId, shelf, workflowId) {
  return {
    id: metadataId,
    workflowId,
    type: ShelfConstants.SHOW_POST_ADD_TO_SHELF_VIEW,
    payload: {
      id: metadataId,
      existsOnShelf: true
    },
    shelf
  };
}

export default {
  addToShelf,
  batchAddToShelf,
  updateShelfItems,
  removeShelfItems,
  searchUserShelf,
  importILSBibs,
  fetchAdditionalFilters,
  showPostAddToShelfView
};



// WEBPACK FOOTER //
// ./app/actions/ShelfActions.js